/**
 * Sky Brief - One-line daily celestial pulse shown at the top of the
 * student dashboard. Pulls the same /api/celestial/data feed as the
 * Observatory and renders it as a compact, glanceable banner.
 *
 *   🌕 Day 14 of Ziv • Preparation Day • Full Moon Witness •
 *      Sundown 7:48 PM • 1 day to High Sabbath
 *
 * Tap → jumps to the Celestial Observatory section.
 */

import { useMemo } from "react";
import { useCelestial } from "../contexts/CelestialContext";

function formatLocalTime(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return null;
  }
}

export default function SkyBrief({ onJumpToObservatory }) {
  // Centralized data — same /api/celestial/data feed all dashboard
  // widgets consume, so values stay in lock-step.
  const { celestialData: data } = useCelestial();

  const brief = useMemo(() => {
    if (!data) return null;
    const { moon, enochian } = data;
    const biblical = data.biblical || {};
    const biblicalDate = biblical.date || {};
    const sun = data.sun || {};

    // 2026-06-05: bind to biblical.date.day (solar Abib-anchor — the
    // Watchman canonical day) instead of enochian.current_lunar_day
    // (moon-age derived). The two diverge by ~1 day around the crescent
    // offset window, which produced the "Day 18 of Ziv" bug while the
    // Watchman correctly showed "Day 19 of Ziv".
    const lunarDay = biblicalDate.day ?? enochian.current_lunar_day;
    const monthName = biblicalDate.month_name || "";
    const isFullMoonWitness =
      (moon?.illumination ?? 0) >= 95 ||
      (typeof lunarDay === "number" && lunarDay >= 14 && lunarDay <= 16);
    const phaseLabel = isFullMoonWitness ? "Full Moon Witness" : moon?.phase_name;

    // Day-status label aligned with Observatory overrides
    let statusLabel;
    if (lunarDay === 14) statusLabel = "Preparation Day";
    else if (enochian.is_high_sabbath) statusLabel = "High Sabbath";
    else if (biblical.sabbath?.is_preparation) statusLabel = "Preparation Day";
    else statusLabel = "Curriculum Active";

    // Countdown to next upcoming Mo'ed (matches Watchman). 2026-06-05:
    // previously computed days-to-monthly-15 and labeled it "High
    // Sabbath" — misleading. Now sourced from biblical.moadim.upcoming[0]
    // (sorted ascending) so the home banner mirrors the Watchman exactly
    // (e.g., "27 days to Shavuot" instead of "27 days to High Sabbath").
    let countdown = null;
    const nextMoed = (biblical.moadim?.upcoming || [])[0];
    if (nextMoed && typeof nextMoed.days_until === "number") {
      const d = nextMoed.days_until;
      const name = nextMoed.name;
      if (d === 0) countdown = `${name} today`;
      else if (d === 1) countdown = `1 day to ${name}`;
      else countdown = `${d} days to ${name}`;
    }

    // Moon emoji
    const illum = moon?.illumination ?? 0;
    const waxing = !!moon?.is_waxing;
    let moonEmoji = "🌑";
    if (illum >= 95) moonEmoji = "🌕";
    else if (illum >= 50) moonEmoji = waxing ? "🌔" : "🌖";
    else if (illum >= 5) moonEmoji = waxing ? "🌒" : "🌘";

    const sundown = formatLocalTime(sun.sunset);

    return {
      moonEmoji,
      lunarDay,
      monthName,
      statusLabel,
      phaseLabel,
      sundown,
      countdown,
    };
  }, [data]);

  if (!brief) return null;

  const statusColor =
    brief.statusLabel === "High Sabbath"
      ? "#FFA500"
      : brief.statusLabel === "Preparation Day"
      ? "#CD853F"
      : "#90EE90";

  return (
    <div
      data-testid="sky-brief-banner"
      onClick={() => onJumpToObservatory && onJumpToObservatory()}
      style={{
        background: "linear-gradient(90deg, #0a0a1a 0%, #1a1a2e 60%, #0a0a1a 100%)",
        border: "1px solid #2a2a4a",
        borderLeft: `3px solid ${statusColor}`,
        borderRadius: "10px",
        padding: "10px 16px",
        marginBottom: "14px",
        cursor: onJumpToObservatory ? "pointer" : "default",
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "10px",
        fontSize: "0.9rem",
        color: "#ddd",
        fontFamily: "Georgia, serif",
        transition: "border-color 0.25s ease",
      }}
      title="Open Celestial Observatory"
    >
      <span style={{ fontSize: "1.4rem", lineHeight: 1 }}>{brief.moonEmoji}</span>
      <span data-testid="sky-brief-day" style={{ color: "#FFD700", fontWeight: 600 }}>
        Day {brief.lunarDay ?? "—"} of {brief.monthName || "the month"}
      </span>
      <span style={{ color: "#555" }}>•</span>
      <span data-testid="sky-brief-status" style={{ color: statusColor, fontWeight: 600 }}>
        {brief.statusLabel}
      </span>
      <span style={{ color: "#555" }}>•</span>
      <span data-testid="sky-brief-phase" style={{ color: "#87CEEB" }}>
        {brief.phaseLabel}
      </span>
      {brief.sundown && (
        <>
          <span style={{ color: "#555" }}>•</span>
          <span data-testid="sky-brief-sundown" style={{ color: "#aaa" }}>
            Sundown {brief.sundown}
          </span>
        </>
      )}
      {brief.countdown && (
        <>
          <span style={{ color: "#555" }}>•</span>
          <span data-testid="sky-brief-countdown" style={{ color: "#DDA0DD", fontWeight: 600 }}>
            {brief.countdown}
          </span>
        </>
      )}
      {onJumpToObservatory && (
        <span
          style={{
            marginLeft: "auto",
            color: "#888",
            fontSize: "0.75rem",
            whiteSpace: "nowrap",
          }}
        >
          Open Observatory →
        </span>
      )}
    </div>
  );
}
